import { Sun, Moon, Monitor } from "lucide-react";
import { Button } from "../components/button";
import { useTheme } from "./useTheme";
import type { ThemeMode } from "./types";

const modeOptions: { value: ThemeMode; label: string; icon: React.ReactNode }[] = [
  { value: "light", label: "Light", icon: <Sun className="size-4" /> },
  { value: "dark", label: "Dark", icon: <Moon className="size-4" /> },
  { value: "auto", label: "System", icon: <Monitor className="size-4" /> },
];

export function ThemeSelector() {
  const { mode, setMode, colorSchemeId, setColorScheme, colorSchemes } = useTheme();

  return (
    <div className="space-y-4">
      {/* Mode toggle */}
      <fieldset>
        <legend className="mb-2 text-sm font-medium">Appearance</legend>
        <div className="flex gap-1 rounded-md border p-1" role="radiogroup" aria-label="Theme mode">
          {modeOptions.map((option) => (
            <Button
              key={option.value}
              variant={mode === option.value ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setMode(option.value)}
              role="radio"
              aria-checked={mode === option.value}
              aria-label={option.label}
            >
              {option.icon}
              <span className="sr-only lg:not-sr-only lg:ml-1">{option.label}</span>
            </Button>
          ))}
        </div>
      </fieldset>

      {/* Color scheme selector */}
      <fieldset>
        <legend className="mb-2 text-sm font-medium">Color scheme</legend>
        <div className="grid grid-cols-5 gap-2" role="radiogroup" aria-label="Color scheme">
          {colorSchemes.map((scheme) => (
            <button
              key={scheme.id}
              type="button"
              onClick={() => setColorScheme(scheme.id)}
              role="radio"
              aria-checked={colorSchemeId === scheme.id}
              aria-label={scheme.name}
              className="hover:bg-accent focus-visible:ring-ring aria-checked:border-primary aria-checked:ring-primary flex flex-col items-center gap-1 rounded-md border p-2 text-xs transition-colors focus-visible:ring-2 focus-visible:outline-none aria-checked:ring-1"
            >
              <span
                className="size-6 rounded-full border"
                style={{ backgroundColor: scheme.light.primary }}
                aria-hidden="true"
              />
              <span className="truncate">{scheme.name}</span>
            </button>
          ))}
        </div>
      </fieldset>
    </div>
  );
}
